import React, { FC, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { InfoOutlined, KeyboardArrowDown } from "@mui/icons-material";
import ArrowPopper from "components/app/ArrowPopper";
import { Grid } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useOrgInvoiceArr } from "../hooks";
import { AuthProvider } from "pages";
import { ROLE } from "constant";
import { classHOC } from "components/app/ClassWrapper";

const OrganizationInvoiceArrCellRenderer: FC<
  ICellRendererParams & { disablePopover?: boolean }
> = ({ data, value, node, disablePopover = true }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const { data: orgData } = useOrgInvoiceArr({
    anchorEl: anchorEl,
    viewType: "all",
    organizationUnitId: data?.organizationUnitId,
    includeInactive: data?.organizationStatus
      ? data?.organizationStatus === "Inactive"
        ? 1
        : 0
      : 0,
    sort: "invoiceArrTotal:desc",
    pageNum: 0,
    pageSize: 500,
  });

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const authDetail = useContext(AuthProvider);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  if (node.rowPinned) {
    return <>-</>;
  }

  const canOpenPopover = !disablePopover && authDetail?.role !== ROLE.COLLAB_SALES_ROLE;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: canOpenPopover ? "pointer" : "auto",
        }}
        ref={rootRef}
        onClick={canOpenPopover ? handleClick : undefined}
      >
        {value}
        {canOpenPopover ? <KeyboardArrowDown style={{ fontSize: "1rem" }} /> : null}
      </div>
      {anchorEl && canOpenPopover ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom"
          disablePortal={false}
        >
          <div ref={popRef}>
            <Grid
              container={true}
              direction="column"
            >
              {orgData ? (
                <>
                  {orgData?.length >= 1 && data?.organizationStatus ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <InfoOutlined
                        fontSize="small"
                        color="disabled"
                      />
                      <span style={{ marginLeft: 5, fontSize: 14 }}>
                        {data?.organizationStatus === "Active"
                          ? "Active Accounts Only"
                          : "All accounts are Inactive"}
                      </span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      maxHeight: 200,
                      paddingRight: 6,
                      overflowY: "auto",
                    }}
                  >
                    {[
                      ...orgData.filter((x) => x?.accountId === data?.accountId),
                      ...orgData.filter((x) => x?.accountId !== data?.accountId),
                    ]?.map((item, i) => (
                      <Grid
                        item={true}
                        container={true}
                        key={item?.accountKey}
                        justifyContent="space-between"
                        style={{
                          marginTop: i === 0 ? 0 : 4,
                          flexWrap: "nowrap",
                        }}
                        alignItems="center"
                      >
                        <Grid
                          item={true}
                          style={{
                            color: "#666",
                            fontSize: 12,
                            lineHeight: "24px",
                            width: 203,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <div style={{ display: "flex" }}>
                            <a
                              href={`/spng/summary?accountId=${item?.accountId}`}
                              style={{
                                display: "inline-block",
                                whiteSpace: "pre",
                                maxWidth: 203,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {`${i + 1}. ${item?.accountName}`}
                            </a>
                            <span style={{ color: "#007AA3", marginLeft: 5 }}>
                              {item.accountId === data.accountId ? "(Current)" : ""}
                            </span>
                          </div>
                        </Grid>
                        <Grid
                          item={true}
                          style={{
                            color: "#333",
                            fontSize: 12,
                            lineHeight: "24px",
                            fontWeight: 600,
                            marginLeft: 60,
                          }}
                        >
                          $
                          {item?.invoiceArrTotal?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Grid>
                      </Grid>
                    ))}
                  </div>
                </>
              ) : !orgData && anchorEl ? (
                <>
                  {Array.from({ length: 3 }).map((x, i) => (
                    <Skeleton
                      key={`${x}${i}`}
                      width={280}
                      height={30}
                      variant="rectangular"
                      style={{ margin: "6px 12px" }}
                    />
                  ))}
                </>
              ) : null}

              {orgData?.length === 0 ? <span>No value available</span> : null}
            </Grid>
          </div>
        </ArrowPopper>
      ) : null}
    </>
  );
};

export default classHOC(OrganizationInvoiceArrCellRenderer);
